import {
  allQuestsDone, applyQuestEvent, bumpQuestStreak, emptyQuestState, getCategoryMeta, getLevelForXP, getXPForNextLevel, liveQuestStreak, questDayFor, questDone, questsFor,
  type DuelOutcome, type Quest, type QuestEvent, type QuestState,
} from '@brainfart/shared';
import { queueLevelUps } from './levelUp';

// All persistent, account-free player progression lives here in one place,
// backed by localStorage. No auth/DB yet — this is the "90% of the stickiness,
// 0% of the login friction" layer. A future accounts system can hydrate from
// and sync back to this same shape.

const PROFILE_KEY = 'bf_profile';

export interface RivalRecord {
  wins: number;
  losses: number;
  ties: number;
  // From duels, so the rivals list can show them and offer a one-tap rematch.
  avatar?: string;
  lastCode?: string;
  lastCategory?: string;
  lastAt?: number;
}

// 1v1 duel record per category. `streak` is the current run of wins.
export interface DuelRecord {
  wins: number;
  losses: number;
  ties: number;
  streak: number;
  bestStreak: number;
}

export interface Profile {
  categoryXP: Record<string, number>;
  streak: { count: number; lastDate: string | null };
  stats: {
    soloGames: number;
    dailyGames: number;
    bestSoloScore: number;
    totalCorrect: number;
    totalAnswered: number;
  };
  rivalries: Record<string, RivalRecord>;
  duels: Record<string, DuelRecord>;
  achievements: Record<string, number>; // id -> when unlocked
  quests: QuestState; // today's quest counters and the streak of days with every quest done (shared/src/quests.ts)
}

const EMPTY_PROFILE: Profile = {
  categoryXP: {},
  streak: { count: 0, lastDate: null },
  stats: { soloGames: 0, dailyGames: 0, bestSoloScore: 0, totalCorrect: 0, totalAnswered: 0 },
  rivalries: {},
  duels: {},
  achievements: {},
  quests: emptyQuestState(),
};

export function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const da = Date.UTC(ay, am - 1, ad);
  const db = Date.UTC(by, bm - 1, bd);
  return Math.round((db - da) / 86400000);
}

export function getProfile(): Profile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return structuredClone(EMPTY_PROFILE);
    const parsed = JSON.parse(raw) as Partial<Profile>;
    // Merge against defaults so older/partial stored shapes never crash reads.
    return {
      categoryXP: parsed.categoryXP || {},
      streak: parsed.streak || { count: 0, lastDate: null },
      stats: { ...EMPTY_PROFILE.stats, ...(parsed.stats || {}) },
      rivalries: parsed.rivalries || {},
      duels: parsed.duels || {},
      achievements: parsed.achievements || {},
      quests: parsed.quests?.day && parsed.quests?.streak ? parsed.quests : emptyQuestState(),
    };
  } catch {
    return structuredClone(EMPTY_PROFILE);
  }
}

function save(profile: Profile) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    /* ignore quota/availability errors */
  }
}

// Banks XP and celebrates any level crossed (see LevelUpHost).
export function addCategoryXP(deltas: Record<string, number>): Profile {
  const profile = getProfile();
  const ups: { category: string; level: number; xp: number }[] = [];
  for (const [cat, xp] of Object.entries(deltas)) {
    if (!xp) continue;
    const before = profile.categoryXP[cat] || 0;
    const after = before + xp;
    profile.categoryXP[cat] = after;
    const level = getLevelForXP(after, cat);
    if (level > getLevelForXP(before, cat)) ups.push({ category: cat, level, xp: after });
  }
  save(profile);
  queueLevelUps(ups);
  return profile;
}

// Bumps the daily streak for `date`. Same-day repeat is a no-op; a one-day gap
// continues the streak; any larger gap resets it to 1. Returns the new count.
export function bumpStreak(date: string = todayKey()): number {
  const profile = getProfile();
  const last = profile.streak.lastDate;
  if (last === date) return profile.streak.count;

  if (last && daysBetween(last, date) === 1) {
    profile.streak.count += 1;
  } else {
    profile.streak.count = 1;
  }
  profile.streak.lastDate = date;
  save(profile);
  return profile.streak.count;
}

// The streak "expires" if you didn't play yesterday or today — so a stored
// count from three days ago should read as broken, not current.
export function currentStreak(): number {
  const profile = getProfile();
  const last = profile.streak.lastDate;
  if (!last) return 0;
  const gap = daysBetween(last, todayKey());
  return gap <= 1 ? profile.streak.count : 0;
}

export function recordSoloResult(result: { score: number; correct: number; total: number }): void {
  const profile = getProfile();
  profile.stats.soloGames += 1;
  profile.stats.bestSoloScore = Math.max(profile.stats.bestSoloScore, result.score);
  profile.stats.totalCorrect += result.correct;
  profile.stats.totalAnswered += result.total;
  save(profile);
}

// Daily quests. A finished game reports what it did (the right answers, a duel, the Daily, a practice quiz); this applies
// them all at once to today's counters and says which quests that completed, so the player gets one message, not four.
export function getQuestState(date: string = todayKey()): { state: QuestState; streak: number } {
  const { quests } = getProfile();
  return { state: { day: questDayFor(quests.day, date), streak: quests.streak }, streak: liveQuestStreak(quests.streak, date) };
}

export function recordQuestEvents(events: QuestEvent[], date: string = todayKey()): { completed: Quest[]; allDone: boolean; streak: number } {
  const profile = getProfile();
  const before = questDayFor(profile.quests.day, date);
  const after = events.reduce(applyQuestEvent, before);
  const completed = questsFor(date).filter(q => !questDone(before, q) && questDone(after, q));
  const allDone = allQuestsDone(after) && !allQuestsDone(before);
  profile.quests = { day: after, streak: allDone ? bumpQuestStreak(profile.quests.streak, date) : profile.quests.streak };
  save(profile);
  return { completed, allDone, streak: liveQuestStreak(profile.quests.streak, date) };
}

export function saveAchievement(id: string): void {
  const profile = getProfile();
  if (profile.achievements[id]) return;
  profile.achievements[id] = Date.now();
  save(profile);
}

// Every answered duel question counts toward overall accuracy too.
export function recordAnswers(correct: number, total: number): void {
  const profile = getProfile();
  profile.stats.totalCorrect += correct;
  profile.stats.totalAnswered += total;
  save(profile);
}

export function recordDailyResult(result: { correct: number; total: number }): void {
  const profile = getProfile();
  profile.stats.dailyGames += 1;
  profile.stats.totalCorrect += result.correct;
  profile.stats.totalAnswered += result.total;
  save(profile);
}

export function recordRivalry(
  opponent: string,
  outcome: 'win' | 'loss' | 'tie',
  extra: { avatar?: string | null; code?: string; category?: string } = {},
): void {
  const name = opponent.trim();
  if (!name) return;
  const profile = getProfile();
  const rec: RivalRecord = profile.rivalries[name] || { wins: 0, losses: 0, ties: 0 };
  if (outcome === 'win') rec.wins += 1;
  else if (outcome === 'loss') rec.losses += 1;
  else rec.ties += 1;
  if (extra.avatar) rec.avatar = extra.avatar;
  if (extra.code && extra.category) {
    rec.lastCode = extra.code;
    rec.lastCategory = extra.category;
  }
  rec.lastAt = Date.now();
  profile.rivalries[name] = rec;
  save(profile);
}

// People you've played, most recent first.
export function getRivals(limit = 8): Array<RivalRecord & { name: string }> {
  return Object.entries(getProfile().rivalries)
    .map(([name, rec]) => ({ name, ...rec }))
    .sort((a, b) => (b.lastAt ?? 0) - (a.lastAt ?? 0) || (b.wins + b.losses + b.ties) - (a.wins + a.losses + a.ties))
    .slice(0, limit);
}

// The category you have the most XP in (null for a brand-new player).
export function bestCategory(): string | null {
  const [best] = Object.entries(getProfile().categoryXP)
    .filter(([key, xp]) => xp > 0 && getCategoryMeta(key))
    .sort((a, b) => b[1] - a[1]);
  return best?.[0] ?? null;
}

export function getDuelRecord(category: string): DuelRecord {
  return getProfile().duels[category] ?? { wins: 0, losses: 0, ties: 0, streak: 0, bestStreak: 0 };
}

export function recordDuel(category: string, outcome: DuelOutcome): DuelRecord {
  const profile = getProfile();
  const rec = { ...getDuelRecord(category), ...profile.duels[category] };
  if (outcome === 'win') {
    rec.wins += 1;
    rec.streak += 1;
    rec.bestStreak = Math.max(rec.bestStreak, rec.streak);
  } else {
    if (outcome === 'loss') rec.losses += 1;
    else rec.ties += 1;
    rec.streak = 0;
  }
  profile.duels[category] = rec;
  save(profile);
  return rec;
}

export interface CategoryLevelInfo {
  key: string;
  xp: number;
  level: number;
  xpToNext: number | null;
  progress: number; // 0-1 through the current level (1 at max level)
}

// How far through the current level: 0 just after levelling up, 1 at the next.
export function levelProgress(xp: number, category: string): number {
  const thresholds = getCategoryMeta(category)?.xpThresholds ?? [];
  const level = getLevelForXP(xp, category);
  const start = thresholds[level - 1] ?? 0;
  const next = thresholds[level];
  if (next === undefined) return 1;
  return Math.max(0, Math.min(1, (xp - start) / (next - start)));
}

export function getCategoryLevels(): CategoryLevelInfo[] {
  const profile = getProfile();
  return Object.entries(profile.categoryXP)
    .map(([key, xp]) => ({
      key,
      xp,
      level: getLevelForXP(xp, key),
      xpToNext: getXPForNextLevel(xp, key),
      progress: levelProgress(xp, key),
    }))
    .sort((a, b) => b.xp - a.xp);
}
